import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Button from '../../../../components/common/Button/index'
import Select from '../../../../components/common/Select'

import MiniHeader from '../../../../components/layout/MiniHeader/index'
import { fetchProperties } from '../../../../state/redux/actions/propertyActions'
import useAppSelector from '../../../../state/redux/hooks/index'
import { TfiRulerAlt2 } from 'react-icons/tfi'
import { IoIosArrowDown } from 'react-icons/io'
import { IoEye } from 'react-icons/io5'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'

const sortOptions = [
  { value: 'All', label: 'Sort by' },
  { value: 'new', label: 'New' },
  { value: 'old', label: 'Old' },
]

const filterOptions = [
  { value: 'all', label: 'Property Type' },
  { value: 'VILLA', label: 'Villa' },
  { value: 'APARTMENT', label: 'Apartment' },
]

const PropertyList = () => {
  const dispatch = useDispatch()
  const { properties, loading, error } = useAppSelector(state => state.property)
  const [activeTab, setActiveTab] = useState('all')
  const [sortValue, setSortValue] = useState('')
  const [filterValue, setFilterValue] = useState('all')
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  useEffect(() => {
    dispatch(fetchProperties())
  }, [dispatch])

  // Filter properties by date range
  const filterPropertiesByDate = (propertyList, startDate, endDate) => {
    if (!startDate || !endDate || !propertyList || !Array.isArray(propertyList)) {
      return propertyList || []
    }

    return propertyList.filter(property => {
      const propertyDate = new Date(property.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return propertyDate >= start && propertyDate <= end
    })
  }

  const filteredProperties = useMemo(() => {
    let result = [...properties]

    // Apply date filter first if actively filtering
    if (isFiltering && dateFilter) {
      result = filterPropertiesByDate(result, dateFilter.startDate, dateFilter.endDate)
    }

    // Apply tab filter
    if (activeTab !== 'all') {
      result = result.filter(property => {
        if (activeTab === 'live') return property.status === 'LIVE'
        if (activeTab === 'decline') return property.status === 'DECLINED'
        if (activeTab === 'draft') return property.status === 'REVIEWING'
        return true
      })
    }
    console.log('activeTab:', activeTab)
    console.log('result AFTER TAB FILTER:', result)
    console.log('filterValue:', filterValue)

    // Apply property type filter
    if (filterValue !== 'all') {
      result = result.filter(property => property.property_type === filterValue)
    }

    // Apply sort
    if (sortValue) {
      result.sort((a, b) => {
        const dateA = new Date(a.createdAt)
        const dateB = new Date(b.createdAt)
        return sortValue === 'new' ? dateB - dateA : dateA - dateB
      })
    }

    return result
  }, [activeTab, filterValue, properties, sortValue, dateFilter, isFiltering])

  const handleFilterChange = e => {
    const value = e.target.value
    setFilterValue(value)
  }

  const handleSortChange = e => {
    const value = e.target.value
    setSortValue(value)
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
  }

  console.log('properties:', filteredProperties.building_unit_size)
  return (
    <>
      <MiniHeader
        title="Property Management"
        data={properties}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] flex-1 w-[1040px] px-4 py-2 right-8 bg-transparent">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && (properties?.length ?? 0) === 0 && (
          <Card variant="outlined" className="text-center p-8 border border-border rounded-md max-w-md w-full">
            <Typography variant="body" className=" text-secondary ">
              No properties found. Add a new property to get started.
            </Typography>
          </Card>
        )}
        {!loading && !error && properties.length > 0 && (
          <>
            {/* tabs header */}
            <div className="my-4 flex justify-between items-center">
              <div className="flex flex-col">
                <Typography variant="h4" className=" px-4">
                  Total Listings
                </Typography>
                <Typography variant="h4" className=" px-4">
                  {isFiltering ? filteredProperties.length : properties.length}
                </Typography>
              </div>
              <div className="flex gap-2">
                {/* sort and filter */}
                <div className="flex gap-4 items-center my-4">
                  <div className="flex">
                    <Select
                      options={sortOptions}
                      value={sortValue}
                      icon={<IoIosArrowDown />}
                      onChange={handleSortChange}
                      className=""
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      options={filterOptions}
                      value={filterValue}
                      icon={<IoIosArrowDown />}
                      onChange={handleFilterChange}
                      className=""
                    />
                  </div>
                </div>
                {/* tabs */}
                <div className="flex gap-2 p-2 bg-primary rounded-2xl h-14 mt-2">
                  {['all', 'live', 'decline', 'draft'].map(tab => (
                    <Button
                      key={tab}
                      size="medium"
                      variant={activeTab === tab ? 'secondary' : null}
                      className={
                        activeTab === tab
                          ? 'bg-white text-dark rounded-xl focus:ring-0'
                          : 'shadow-transparent text-white no-underline outline-none'
                      }
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab === 'all' && 'All'}
                      {tab === 'live' && 'Live'}
                      {tab === 'decline' && 'Decline '}
                      {tab === 'draft' && 'Draft '}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white w-full p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Typography className="">Most Viewed</Typography>
                  <IoEye className="text-xl" />
                  (223)
                </div>
                <Link to="#">
                  <Typography className="text-primary">See More</Typography>
                </Link>
              </div>
              <div className="grid gap-4 grid-cols-3 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
                {filteredProperties.map(property => (
                  <Link to={`/properties/${property._id}`} state={{ fromTab: activeTab, property }} key={property._id}>
                    <Card
                      variant="outlined"
                      className="transition-transform duration-200 ease-in-out hover:-translate-y-1 rounded-2xl overflow-hidden bg-white shadow-lg border-0 max-w-xs"
                    >
                      <div className="flex flex-col h-full">
                        {/* Property Image with Overlays */}
                        <div 
                          className="relative h-64 bg-cover bg-center" 
                          style={{ 
                            backgroundImage: property.images && property.images.length > 0 
                              ? `url("${property.images[0].trim().replace(/`/g, '')}")` 
                              : `url("/assets/images/house.jpg")`
                          }}
                        >
                          {/* Image indicators for multiple images */}
                          {property.images && property.images.length > 1 && (
                            <div className="absolute top-4 right-4 flex gap-1">
                              {property.images.slice(0, 3).map((_, index) => (
                                <div key={index} className="w-2 h-2 bg-white bg-opacity-60 rounded-full"></div>
                              ))}
                              {property.images.length > 3 && (
                                <span className="text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">+{property.images.length - 3}</span>
                              )}
                            </div>
                          )}
                          {/* Dark overlay for better text readability */}
                          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                          
                          {/* Property Title */}
                          <div className="absolute top-4 left-4 right-4">
                            <Typography variant="h4" className="text-white font-bold text-lg leading-tight">
                              {property.property_title}
                            </Typography>
                          </div>
                          
                          {/* Availability Badge */}
                          <div className="absolute top-16 left-4">
                            <span className=" text-white px-3 py-1 rounded-md text-xs font-semibold  tracking-wide">
                              {property.availability}
                            </span>
                          </div>
                          
                          {/* Price - Bottom of image */}
                          <div className="absolute bottom-12 left-4 right-4">
                            <Typography variant="h5" className="text-white font-bold text-xl">
                              SAR {property.property_price?.toLocaleString()} /monthly
                            </Typography>
                          </div>
                          
                          {/* Description */}
                          <div className="absolute bottom-4 left-4 right-4">
                            <Typography variant="body2" className="text-white text-sm opacity-90">
                              {property.property_description || "Modern office space"}
                            </Typography>
                          </div>
                        </div>
                        
                        {/* Bottom Action Buttons */}
                        <div className="flex h-12">
                          {/* Left Button - Listing Type */}
                          <div className="flex-1 bg-teal-500 hover:bg-teal-600 flex items-center justify-center">
                            <Typography className="text-white text-sm font-semibold">
                              For {property.listing_type?.toLowerCase() || 'rent'}
                            </Typography>
                          </div>
                          
                          {/* Right Button - Size */}
                          <div className="flex-1 bg-gray-100 hover:bg-gray-200 flex items-center justify-center gap-2">
                            <TfiRulerAlt2 className="text-gray-600 text-sm" />
                            <Typography className="text-gray-700 text-sm font-medium">
                              {property.building_unit_size || '1300'} {property.building_unit_size_unit || 'sq.m'}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
                {filteredProperties.length === 0 && (
                  <Card variant="outlined" className="text-center p-8 border border-border rounded max-w-md w-full">
                    <Typography variant="body" className=" text-secondary ">
                      No properties found. Add a new property to get started.
                    </Typography>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default PropertyList
